import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from 'react-router-dom';
import { auth } from './firebase';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import { db, storage } from './firebase';
import { collection, addDoc, Timestamp, doc, getDoc, setDoc, getDocs, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import './App.css';
import JSZip from 'jszip';

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
      onLogin();
    } catch (err) {
      setError('Invalid email or password');
    }
  };

  return (
    <div className="login-container">
      <h2>Admin Login</h2>
      <form onSubmit={handleSubmit}>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button type="submit">Login</button>
        {error && <p className="error">{error}</p>}
      </form>
    </div>
  );
}

function AdminPanel({ onLogout }) {
  const [form, setForm] = useState({
    registration: '',
    make: '',
    model: '',
    colour: '',
    fuelType: '',
    transmission: '',
    damageDescription: '',
    collectionAddress: '',
    deliveryAddress: '',
    date: '',
    notes: '',
    images: [],
    password: '',
    message: ''
  });
  const [uploading, setUploading] = useState(false);
  const [vehicleLink, setVehicleLink] = useState('');
  const [error, setError] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [addImagesInputs, setAddImagesInputs] = useState({});
  const [addImagesFiles, setAddImagesFiles] = useState({});

  useEffect(() => {
    const fetchVehicles = async () => {
      const snap = await getDocs(collection(db, 'vehicles'));
      setVehicles(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchVehicles();
  }, [refresh]);

  // Generate a unique password for each vehicle
  const generatePassword = (reg) => {
    const safeReg = reg.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    const rand = Math.random().toString(36).substring(2, 8);
    return `${safeReg}_${rand}`;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'images') {
      setForm((prev) => ({ ...prev, images: files }));
    } else if (name === 'registration') {
      setForm((prev) => ({ ...prev, registration: value, password: generatePassword(value) }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');
    try {
      console.log('Starting upload...');
      // Upload images to Firebase Storage
      const imageUrls = [];
      if (!form.images || form.images.length === 0) {
        throw new Error('No images selected');
      }
      for (let i = 0; i < form.images.length; i++) {
        const image = form.images[i];
        console.log('Uploading image:', image);
        const imageRef = ref(storage, `vehicles/${form.registration.replace(/[^A-Z0-9]/gi, '').toUpperCase()}_${Date.now()}_${image.name}`);
        await uploadBytes(imageRef, image);
        const url = await getDownloadURL(imageRef);
        imageUrls.push(url);
        console.log('Uploaded image URL:', url);
      }
      // Generate a short ID for docId
      const shortId = Math.random().toString(36).substring(2, 8);
      const safeReg = form.registration.replace(/[^A-Z0-9]/gi, '').toUpperCase();
      const docId = `${safeReg}_${shortId}`;
      // Save vehicle data to Firestore with custom ID and password/message
      await setDoc(doc(db, 'vehicles', docId), {
        registration: form.registration,
        make: form.make,
        model: form.model,
        colour: form.colour,
        fuelType: form.fuelType,
        transmission: form.transmission,
        damageDescription: form.damageDescription,
        collectionAddress: form.collectionAddress,
        deliveryAddress: form.deliveryAddress,
        date: form.date,
        notes: form.notes,
        images: imageUrls,
        createdAt: Timestamp.now(),
        password: form.password,
        message: form.message
      });
      setVehicleLink(`${window.location.origin}/vehicle/${docId}`);
      setForm({
        registration: '',
        make: '',
        model: '',
        colour: '',
        fuelType: '',
        transmission: '',
        damageDescription: '',
        collectionAddress: '',
        deliveryAddress: '',
        date: '',
        notes: '',
        images: [],
        password: '',
        message: ''
      });
      // Clear the file input manually
      document.querySelector('input[name="images"]').value = '';
      console.log('Vehicle added with ID:', docId);
      setRefresh(r => r + 1);
    } catch (err) {
      setError('Failed to upload vehicle.');
      console.error('Upload error:', err);
    }
    setUploading(false);
  };

  // Add images to existing vehicle
  const handleAddImages = async (vehicleId) => {
    const files = addImagesFiles[vehicleId] || [];
    if (!files.length) return;
    setUploading(true);
    try {
      const newUrls = [];
      for (let i = 0; i < files.length; i++) {
        const image = files[i];
        const imageRef = ref(storage, `vehicles/${vehicleId}_${Date.now()}_${image.name}`);
        await uploadBytes(imageRef, image);
        const url = await getDownloadURL(imageRef);
        newUrls.push(url);
      }
      const docRef = doc(db, 'vehicles', vehicleId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const prev = docSnap.data().images || [];
        await updateDoc(docRef, { images: [...prev, ...newUrls] });
      }
      setAddImagesFiles(f => ({ ...f, [vehicleId]: [] }));
      setAddImagesInputs(f => ({ ...f, [vehicleId]: 1 }));
      setRefresh(r => r + 1);
    } catch (err) {
      alert('Failed to add images');
    }
    setUploading(false);
  };

  // Remove image from vehicle
  const handleRemoveImage = async (vehicleId, url) => {
    setUploading(true);
    try {
      // Remove from storage
      const path = decodeURIComponent(url.split('/o/')[1].split('?')[0]);
      await deleteObject(ref(storage, path));
      // Remove from Firestore
      const docRef = doc(db, 'vehicles', vehicleId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const prev = docSnap.data().images || [];
        await updateDoc(docRef, { images: prev.filter(u => u !== url) });
      }
      setRefresh(r => r + 1);
    } catch (err) {
      alert('Failed to remove image');
    }
    setUploading(false);
  };

  // Delete vehicle
  const handleDeleteVehicle = async (vehicleId, images) => {
    setUploading(true);
    try {
      // Delete all images from storage
      for (let url of images) {
        const path = decodeURIComponent(url.split('/o/')[1].split('?')[0]);
        await deleteObject(ref(storage, path));
      }
      await deleteDoc(doc(db, 'vehicles', vehicleId));
      setRefresh(r => r + 1);
    } catch (err) {
      alert('Failed to delete vehicle');
    }
    setUploading(false);
  };

  // Add more file input lines for add images
  const handleAddInputLine = (vehicleId) => {
    setAddImagesInputs(f => ({ ...f, [vehicleId]: (f[vehicleId] || 1) + 1 }));
  };

  // Handle file input change for add images
  const handleAddImagesChange = (vehicleId, e, idx) => {
    const files = Array.from(e.target.files);
    setAddImagesFiles(f => {
      const prev = f[vehicleId] || [];
      const newArr = [...prev];
      newArr[idx] = files;
      return { ...f, [vehicleId]: newArr.flat() };
    });
  };

  return (
    <div className="admin-panel-modern">
      <div className="admin-header">
        <h2>Admin Panel</h2>
        <button className="logout-btn" onClick={onLogout}>Logout</button>
      </div>
      <div className="add-vehicle-card">
        <h3>Add Vehicle</h3>
        <form onSubmit={handleSubmit} className="vehicle-form">
          <input name="registration" placeholder="Registration Number" value={form.registration} onChange={handleChange} required />
          <input name="make" placeholder="Make" value={form.make} onChange={handleChange} required />
          <input name="model" placeholder="Model" value={form.model} onChange={handleChange} required />
          <input name="colour" placeholder="Colour" value={form.colour} onChange={handleChange} required />
          <input name="fuelType" placeholder="Fuel Type" value={form.fuelType} onChange={handleChange} required />
          <input name="transmission" placeholder="Transmission" value={form.transmission} onChange={handleChange} required />
          <textarea name="damageDescription" placeholder="Damage Description" value={form.damageDescription} onChange={handleChange} required />
          <input name="collectionAddress" placeholder="Collection Address" value={form.collectionAddress} onChange={handleChange} required />
          <input name="deliveryAddress" placeholder="Delivery Address" value={form.deliveryAddress} onChange={handleChange} required />
          <input name="date" type="date" placeholder="Date" value={form.date} onChange={handleChange} required />
          <textarea name="notes" placeholder="Notes" value={form.notes} onChange={handleChange} />
          <input name="images" type="file" multiple accept="image/*" onChange={handleChange} required />
          <input name="password" placeholder="Password (auto-generated)" value={form.password} readOnly style={{ background: '#f1f1f1', fontWeight: 'bold' }} />
          <textarea name="message" placeholder="Custom message for client (optional)" value={form.message} onChange={handleChange} />
          <button type="submit" disabled={uploading}>{uploading ? 'Uploading...' : 'Add Vehicle'}</button>
          {error && <p className="error">{error}</p>}
        </form>
        {vehicleLink && (
          <div className="vehicle-link">
            <p>Vehicle added! Share this link with your client:</p>
            <a href={vehicleLink} target="_blank" rel="noopener noreferrer">{vehicleLink}</a>
          </div>
        )}
      </div>
      <div className="vehicle-list">
        {vehicles.map(vehicle => (
          <div className="vehicle-card" key={vehicle.id}>
            <div className="vehicle-main-info">
              <div>
                <strong>{vehicle.registration}</strong> <span className="vehicle-date">{vehicle.date}</span>
              </div>
              <div className="vehicle-link-row">
                <input className="vehicle-link-input" value={`${window.location.origin}/vehicle/${vehicle.id}`} readOnly />
                <button className="copy-btn" onClick={e => { e.preventDefault(); navigator.clipboard.writeText(`${window.location.origin}/vehicle/${vehicle.id}`); }}>Copy Link</button>
              </div>
              <div className="vehicle-actions">
                <button className="delete-btn" onClick={e => { e.preventDefault(); handleDeleteVehicle(vehicle.id, vehicle.images || []); }}>Delete</button>
              </div>
              <div className="vehicle-password-row">
                <span className="vehicle-password-label">Password:</span>
                <span className="vehicle-password-value">{vehicle.password}</span>
              </div>
              {vehicle.message && (
                <div className="vehicle-message-row">
                  <span className="vehicle-message-label">Message:</span>
                  <span className="vehicle-message-value">{vehicle.message}</span>
                </div>
              )}
            </div>
            <div className="vehicle-details">
              <div><strong>Make:</strong> {vehicle.make}</div>
              <div><strong>Model:</strong> {vehicle.model}</div>
              <div><strong>Colour:</strong> {vehicle.colour}</div>
              <div><strong>Fuel Type:</strong> {vehicle.fuelType}</div>
              <div><strong>Transmission:</strong> {vehicle.transmission}</div>
              <div><strong>Damage:</strong> {vehicle.damageDescription}</div>
              <div><strong>Collection:</strong> {vehicle.collectionAddress}</div>
              <div><strong>Delivery:</strong> {vehicle.deliveryAddress}</div>
              <div><strong>Notes:</strong> {vehicle.notes}</div>
            </div>
            <div className="vehicle-images-admin">
              <strong>Images:</strong>
              <div className="vehicle-images-list">
                {vehicle.images && vehicle.images.map((url, idx) => (
                  <div className="vehicle-image-thumb" key={idx}>
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <img src={url} alt="vehicle" />
                    </a>
                    <button className="remove-img-btn" onClick={e => { e.preventDefault(); handleRemoveImage(vehicle.id, url); }}>&times;</button>
                  </div>
                ))}
              </div>
              <div className="add-images-section">
                {[...Array(addImagesInputs[vehicle.id] || 1)].map((_, idx) => (
                  <input key={idx} type="file" multiple accept="image/*" onChange={e => { e.preventDefault(); handleAddImagesChange(vehicle.id, e, idx); }} />
                ))}
                <button className="add-input-btn" onClick={e => { e.preventDefault(); handleAddInputLine(vehicle.id); }}>Add More</button>
                <button className="add-imgs-btn" onClick={e => { e.preventDefault(); handleAddImages(vehicle.id); }} disabled={uploading}>Add Images</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VehiclePage() {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordOk, setPasswordOk] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const docRef = doc(db, 'vehicles', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setVehicle(docSnap.data());
        } else {
          setError('Vehicle not found.');
        }
      } catch (err) {
        setError('Failed to fetch vehicle.');
      }
      setLoading(false);
    };
    fetchVehicle();
  }, [id]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxUrl || !vehicle?.images) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setLightboxUrl(null);
      if (e.key === 'ArrowLeft') setLightboxIdx(idx => (idx - 1 + vehicle.images.length) % vehicle.images.length);
      if (e.key === 'ArrowRight') setLightboxIdx(idx => (idx + 1) % vehicle.images.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxUrl, vehicle]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  if (!vehicle) return null;

  // Password protection
  if (!passwordOk) {
    return (
      <div className="client-vehicle-page">
        <div className="client-header">
          <img src="/RTS_Logo.png" alt="RTS Assist Logo" className="client-logo" />
        </div>
        <div className="client-vehicle-card">
          <h2>Enter Password</h2>
          <input
            type="password"
            value={passwordInput}
            onChange={e => { setPasswordInput(e.target.value); setAttempted(false); }}
            placeholder="Password"
            className="vehicle-password-input"
            style={{ width: '100%', padding: 12, fontSize: '1.1rem', borderRadius: 8, border: '1px solid #ccc', marginBottom: 16 }}
          />
          <button
            className="vehicle-password-btn"
            onClick={() => { setAttempted(true); setPasswordOk(passwordInput === vehicle.password); }}
            style={{ background: '#007aff', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: '1rem', cursor: 'pointer' }}
          >Submit</button>
          {attempted && passwordInput !== vehicle.password && (
            <div style={{ color: '#d32f2f', marginTop: 12 }}>Incorrect password</div>
          )}
        </div>
      </div>
    );
  }

  // Download all images as zip
  const handleDownloadZip = async () => {
    setDownloadingZip(true);
    const zip = new JSZip();
    const imgFolder = zip.folder('images');
    await Promise.all(
      vehicle.images.map(async (url, idx) => {
        const response = await fetch(url);
        const blob = await response.blob();
        imgFolder.file(`image${idx + 1}.jpg`, blob);
      })
    );
    const content = await zip.generateAsync({ type: 'blob' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(content);
    a.download = `${vehicle.registration}_images.zip`;
    a.click();
    setDownloadingZip(false);
  };

  const openLightbox = (idx) => {
    setLightboxIdx(idx);
    setLightboxUrl(vehicle.images[idx]);
  };
  const closeLightbox = () => setLightboxUrl(null);
  const prevImg = () => setLightboxIdx(idx => {
    const newIdx = (idx - 1 + vehicle.images.length) % vehicle.images.length;
    setLightboxUrl(vehicle.images[newIdx]);
    return newIdx;
  });
  const nextImg = () => setLightboxIdx(idx => {
    const newIdx = (idx + 1) % vehicle.images.length;
    setLightboxUrl(vehicle.images[newIdx]);
    return newIdx;
  });

  return (
    <div className="client-vehicle-page">
      <div className="client-header">
        <img src="/RTS_Logo.png" alt="RTS Assist Logo" className="client-logo" />
      </div>
      <div className="client-vehicle-card">
        {vehicle.message && (
          <div className="client-message">{vehicle.message}</div>
        )}
        <h2>Vehicle Details</h2>
        <div className="client-vehicle-details">
          <div><strong>Registration Number:</strong> {vehicle.registration}</div>
          <div><strong>Make:</strong> {vehicle.make}</div>
          <div><strong>Model:</strong> {vehicle.model}</div>
          <div><strong>Colour:</strong> {vehicle.colour}</div>
          <div><strong>Fuel Type:</strong> {vehicle.fuelType}</div>
          <div><strong>Transmission:</strong> {vehicle.transmission}</div>
          <div><strong>Damage Description:</strong> {vehicle.damageDescription}</div>
          <div><strong>Collection Address:</strong> {vehicle.collectionAddress}</div>
          <div><strong>Delivery Address:</strong> {vehicle.deliveryAddress}</div>
          <div><strong>Date:</strong> {vehicle.date}</div>
          <div><strong>Notes:</strong> {vehicle.notes}</div>
        </div>
        <div className="client-vehicle-images">
          <strong>Images:</strong>
          <button className="download-zip-btn" onClick={handleDownloadZip} disabled={downloadingZip} style={{ marginBottom: 12, marginTop: 8 }}>
            {downloadingZip ? 'Preparing Zip...' : 'Download All as Zip'}
          </button>
          <div className="client-images-list">
            {vehicle.images && vehicle.images.map((url, idx) => (
              <div key={idx} className="client-image-thumb-wrap">
                <img
                  src={url}
                  alt={`Vehicle ${idx + 1}`}
                  className="client-image-thumb"
                  onClick={() => openLightbox(idx)}
                  style={{ cursor: 'zoom-in' }}
                />
                <div className="client-image-actions">
                  {/* Download Icon PNG */}
                  <a
                    href={url}
                    download={`image${idx + 1}.jpg`}
                    className="download-img-btn"
                    title="Download image"
                    tabIndex={0}
                    onClick={e => e.stopPropagation()}
                  >
                    <img src="/download_icon.png" alt="Download" className="download-icon-img" width={32} height={32} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {lightboxUrl && (
        <div className="lightbox" onClick={closeLightbox}>
          <button className="lightbox-arrow left" onClick={e => { e.stopPropagation(); prevImg(); }}>&#8592;</button>
          <img src={lightboxUrl} alt="Full Size" className="lightbox-img" />
          <button className="lightbox-arrow right" onClick={e => { e.stopPropagation(); nextImg(); }}>&#8594;</button>
          <button className="lightbox-close" onClick={e => { e.stopPropagation(); closeLightbox(); }}>&times;</button>
        </div>
      )}
    </div>
  );
}

function ShareImageButton({ url }) {
  const [copied, setCopied] = useState(false);
  const handleShare = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <button
      className="share-img-btn"
      title="Copy image link"
      tabIndex={0}
      onClick={handleShare}
      style={{ position: 'relative' }}
    >
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="16" cy="16" r="15" fill="#fff" stroke="#e0e0e5" strokeWidth="2"/>
        <g stroke="#007aff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 10v8"/>
          <path d="M12 14l4-4 4 4"/>
          <rect x="10" y="18" width="12" height="4" rx="2"/>
        </g>
      </svg>
      {copied && (
        <span className="share-tooltip">Copied!</span>
      )}
    </button>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = () => {
    setUser(auth.currentUser);
  };

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
  };

  if (loading) return <div>Loading...</div>;

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to="/admin" /> : <Login onLogin={handleLogin} />}
        />
        <Route
          path="/admin"
          element={user ? <AdminPanel onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/vehicle/:id"
          element={<VehiclePage />}
        />
        <Route
          path="*"
          element={<Navigate to={user ? "/admin" : "/login"} />}
        />
      </Routes>
    </Router>
  );
}

export default App;
